import * as Yup from "yup"
import { DateTime } from "luxon"
import i18n from "@/i18n"

interface ReservationConfiguration {
  minAdvanceBookingTime?: number | null
  maxAdvanceBookingTime?: number | null
  minReservationTime?: number | null
  maxReservationTime?: number | null
}

export const createPublicReservationValidationSchema = (
  config?: ReservationConfiguration | null,
  timezone: string = "UTC",
  editMode: boolean = false,
) => {
  return Yup.object({
    date: Yup.mixed<DateTime>()
      .required(i18n.t("app.public.new_reservation.errors.date_required"))
      .test("min-advance", i18n.t("app.public.venue.validation.min_advance_booking"), function (value) {
        if (editMode) return true
        if (!value || !config?.minAdvanceBookingTime) return true
        const now = DateTime.now().setZone(timezone)
        const diff = value.diff(now, "seconds").seconds
        return diff >= config.minAdvanceBookingTime
      })
      .test("max-advance", i18n.t("app.public.venue.validation.max_advance_booking"), function (value) {
        if (editMode) return true
        if (!value || !config?.maxAdvanceBookingTime) return true
        const now = DateTime.now().setZone(timezone)
        const diff = value.diff(now, "seconds").seconds
        return diff <= config.maxAdvanceBookingTime
      }),
    startTime: Yup.string()
      .required(i18n.t("app.public.new_reservation.errors.start_time_required"))
      .test("not-in-past", i18n.t("app.public.new_reservation.errors.start_time_in_past"), function (value) {
        if (editMode) return true
        if (!value) return true
        const now = DateTime.now().setZone(timezone)
        return DateTime.fromISO(value) > now
      }),
    endTime: Yup.string()
      .required(i18n.t("app.public.new_reservation.errors.end_time_required"))
      .test("after-start", i18n.t("app.public.new_reservation.errors.end_time_too_early"), function (value) {
        const { startTime } = this.parent
        if (!value || !startTime) return true
        return DateTime.fromISO(value) > DateTime.fromISO(startTime)
      })
      .test("min-duration", i18n.t("app.public.venue.validation.min_reservation_time"), function (value) {
        const { startTime } = this.parent
        if (!value || !startTime || !config?.minReservationTime) return true
        const start = DateTime.fromISO(startTime)
        const end = DateTime.fromISO(value)
        const duration = end.diff(start, "seconds").seconds
        return duration >= config.minReservationTime
      })
      .test("max-duration", i18n.t("app.public.venue.validation.max_reservation_time"), function (value) {
        const { startTime } = this.parent
        if (!value || !startTime || !config?.maxReservationTime) return true
        const start = DateTime.fromISO(startTime)
        const end = DateTime.fromISO(value)
        const duration = end.diff(start, "seconds").seconds
        return duration <= config.maxReservationTime
      }),
    peopleAmount: Yup.number()
      .required(i18n.t("app.public.new_reservation.errors.people_amount_required"))
      .min(1, i18n.t("app.public.venue.validation.min_people_amount"))
      .max(20, i18n.t("app.public.venue.validation.max_people_amount")),
    tableId: Yup.string().required(i18n.t("app.public.new_reservation.errors.table_required")),
    name: Yup.string().required(i18n.t("app.public.new_reservation.errors.name_required")),
    email: Yup.string()
      .email(i18n.t("app.public.new_reservation.errors.email_invalid"))
      .required(i18n.t("app.public.new_reservation.errors.email_required")),
    phone: Yup.string().required(i18n.t("app.public.new_reservation.errors.phone_required")),
    note: Yup.string(),
    marketingConsent: Yup.boolean(),
    termsAccepted: Yup.boolean().oneOf([true], i18n.t("app.public.new_reservation.errors.terms_accepted_required")),
  })
}
